<template>
    <kiosk-layout>
        <template v-if="callout.forSection('kiosk-tcr-campaigns').length" v-slot:callout>
            <page-callout section="kiosk-tcr-campaigns" />
        </template>
        <template v-slot:title>Campaigns</template>
        <template v-if="props.registrationEnabled" v-slot:actions>
            <div data-test="registration-actions">
                <vx-button href="/kiosk/ten-dlc/campaigns/new" size="lg" data-test="new-campaign">
                    New Campaign <font-awesome-icon widthAuto :icon="faPlus" />
                </vx-button>
            </div>
        </template>

        <template v-slot:sidebar>
            <kiosk-tcr-campaigns-sidebar v-model="fetchParams"></kiosk-tcr-campaigns-sidebar>
        </template>

        <div>
            <kiosk-ten-dlc-controls
                v-model="fetchParams"
                placeholder="Search campaign ID, brand ID or brand name"
                :exporting="exporting"
                @apply="applyFilters"
                @reset="resetFilters"
                @export="exportCsv(fetchParams)"
            ></kiosk-ten-dlc-controls>

            <page-loading v-if="loading && !list.length"></page-loading>

            <empty-state v-else-if="!list.length && !isEqual(defaultFilters(), fetchParams)" @reset="resetFilters" />

            <empty-state v-else-if="!list.length"> No campaigns found. </empty-state>

            <div v-if="list.length">
                <kiosk-tcr-campaigns-table>
                    <kiosk-tcr-campaign-row
                        v-for="campaign in list"
                        :key="campaign.campaign_id"
                        :modelValue="campaign"
                        :registrationEnabled="props.registrationEnabled"
                    ></kiosk-tcr-campaign-row>
                </kiosk-tcr-campaigns-table>

                <div v-if="fetchParams.cursor" class="mt-8">
                    <vx-button
                        @click="loadData()"
                        color="muted-dark"
                        size="xl"
                        class="grow-0"
                        data-test="load-more-btn"
                        :loading="loading"
                    >
                        Load More
                        <font-awesome-icon widthAuto :icon="faSpinner"></font-awesome-icon>
                    </vx-button>
                </div>
            </div>
        </div>
    </kiosk-layout>
</template>

<script lang="ts" setup>
import { faPlus, faSpinner } from '@fortawesome/pro-solid-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/vue-fontawesome';
import { VxButton } from '@voxie/frontend-components';
import { watch } from 'vue';
import EmptyState from '~/components/general/EmptyState.vue';
import PageCallout from '~/components/general/PageCallout.vue';
import PageLoading from '~/components/general/PageLoading.vue';
import KioskLayout from '~/components/kiosk/KioskLayout.vue';
import KioskTenDlcControls from '~/components/kiosk/kiosk-ten-dlc/KioskTenDlcControls.vue';
import { useTenDlcExport } from '~/components/kiosk/kiosk-ten-dlc/composables/useTenDlcExport';
import KioskTcrCampaignRow from '~/components/kiosk/kiosk-ten-dlc/list/KioskTcrCampaignRow.vue';
import KioskTcrCampaignsSidebar from '~/components/kiosk/kiosk-ten-dlc/list/KioskTcrCampaignsSidebar.vue';
import KioskTcrCampaignsTable from '~/components/kiosk/kiosk-ten-dlc/list/KioskTcrCampaignsTable.vue';
import { campaignExportFields } from '~/components/kiosk/kiosk-ten-dlc/tenDlcExport';
import { useCallout } from '~/composables/useCallout';
import { useCursorPagination } from '~/composables/useCursorPagination';
import { useQueryParams } from '~/composables/useQueryString';
import { adminTenDlcClient } from '~/services';
import type { TcrCampaign, TcrListParams } from '~/services/adminTenDlcClient.types';
import { isEqual } from '~/utils/helpers';

const props = withDefaults(defineProps<{ registrationEnabled?: boolean }>(), { registrationEnabled: false });

const callout = useCallout();

// Every sidebar key must be present (as undefined) so isEqual() against the
// live fetchParams keeps working after the sidebar writes undefined values.
const defaultFilters = (): TcrListParams => ({
    query: '',
    source: undefined,
    brand_id: undefined,
    statuses: undefined,
    dca_complete: undefined,
    att_class_buckets: undefined,
    tmobile_tier_states: undefined,
    terms_states: undefined,
    has_phone_numbers: undefined,
    sort_field: 'campaign_id',
    sort_dir: 'asc',
    per_page: 25,
    cursor: null,
});

const { loading, list, fetchParams, fetchRecords } = useCursorPagination<TcrCampaign, TcrListParams>();

const listCancelKey = Symbol('kiosk-tcr-campaigns');

const { exporting, exportCsv } = useTenDlcExport(
    (params) => adminTenDlcClient.getTcrCampaigns(params),
    campaignExportFields,
    'ten-dlc-campaigns.csv'
);

const urlFilters = useQueryParams<TcrListParams>();

const syncUrl = () => {
    Object.assign(urlFilters, {
        query: fetchParams.value.query || undefined,
        brand_id: fetchParams.value.brand_id,
        source: fetchParams.value.source,
        statuses: fetchParams.value.statuses,
        dca_complete: fetchParams.value.dca_complete,
        att_class_buckets: fetchParams.value.att_class_buckets,
        tmobile_tier_states: fetchParams.value.tmobile_tier_states,
        terms_states: fetchParams.value.terms_states,
        has_phone_numbers: fetchParams.value.has_phone_numbers,
    });
};

const loadData = () => {
    syncUrl();
    fetchRecords(adminTenDlcClient.getTcrCampaigns(fetchParams.value, { cancelKey: listCancelKey }));
};

// Seeded before the watcher below is registered so it does not fire a second request.
fetchParams.value = {
    ...defaultFilters(),
    // The query-string parser coerces a digits-only search to a number.
    query: undefined === urlFilters.query ? '' : String(urlFilters.query),
    brand_id: urlFilters.brand_id,
    source: urlFilters.source,
    statuses: urlFilters.statuses,
    dca_complete: urlFilters.dca_complete,
    att_class_buckets: urlFilters.att_class_buckets,
    tmobile_tier_states: urlFilters.tmobile_tier_states,
    terms_states: urlFilters.terms_states,
    has_phone_numbers: urlFilters.has_phone_numbers,
};

// Deep-watching fetchParams would loop forever: fetchRecords writes the next
// cursor back into it after every response. Watch the sidebar filters by name.
watch(
    [
        () => fetchParams.value.source,
        () => fetchParams.value.brand_id,
        () => fetchParams.value.statuses,
        () => fetchParams.value.dca_complete,
        () => fetchParams.value.att_class_buckets,
        () => fetchParams.value.tmobile_tier_states,
        () => fetchParams.value.terms_states,
        () => fetchParams.value.has_phone_numbers,
    ],
    () => {
        fetchParams.value.cursor = null;
        loadData();
    }
);

const applyFilters = () => {
    fetchParams.value = {
        ...fetchParams.value,
        cursor: null,
    };
    loadData();
};

const resetFilters = () => {
    fetchParams.value = defaultFilters();
    loadData();
};

loadData();
</script>
